import React from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Typography from "@material-ui/core/Typography";
import { DataGridItem } from "components/data-grid";
import { SimpleCheckbox } from "components/form/form-checkbox";
import withStyles from "components/with-styles";
import styles from "./styles/MixerSection.css.js";

const MixerSection = ({ intl, values, setValue, classes }) => {
  const handleMixerAutoChange = e => {
    const isChecked = e.target.checked;
    setValue("file.mixer.auto", isChecked);
  };

  const handleMixerVideoReorder = (fromIndex, toIndex) => {
    const currentVideoArray = values.file?.mixer?.video || [];
    const newVideoArray = [...currentVideoArray];
    const [movedItem] = newVideoArray.splice(fromIndex, 1);
    newVideoArray.splice(toIndex, 0, movedItem);
    setValue("file.mixer.video", newVideoArray);
  };

  const handleMixerAudioReorder = (fromIndex, toIndex) => {
    const currentAudioArray = values.file?.mixer?.audio || [];
    const newAudioArray = [...currentAudioArray];
    const [movedItem] = newAudioArray.splice(fromIndex, 1);
    newAudioArray.splice(toIndex, 0, movedItem);
    setValue("file.mixer.audio", newAudioArray);
  };

  return (
    <>
      <DataGridItem big>
        <Typography variant="h6" gutterBottom className={classes.title}>
          {intl.formatMessage({ id: "roomTemplate.mixer.title" }, { defaultMessage: "Mixer Configuration" })}
        </Typography>
        <Typography variant="caption" color="textSecondary" className={classes.instructions}>
          {intl.formatMessage(
            { id: "roomTemplate.mixer.instructions" },
            { defaultMessage: "Configure mixer settings for video and audio processing" },
          )}
        </Typography>
        <SimpleCheckbox
          id="file.mixer.auto"
          name="file.mixer.auto"
          value="auto"
          checked={values.file?.mixer?.auto || false}
          onChange={handleMixerAutoChange}
          label={intl.formatMessage({ id: "roomTemplate.mixer.auto" }, { defaultMessage: "Auto Mixer" })}
        />
      </DataGridItem>
      <DataGridItem big>
        <Typography variant="subtitle1" gutterBottom className={classes.sectionTitle}>
          {intl.formatMessage({ id: "roomTemplate.mixer.video.title" }, { defaultMessage: "Video Sources Order" })}
        </Typography>
        <Typography variant="caption" color="textSecondary" className={classes.sectionInstructions}>
          {intl.formatMessage(
            { id: "roomTemplate.mixer.video.instructions" },
            { defaultMessage: "Drag and drop to reorder video sources" },
          )}
        </Typography>
        <div className={classes.videoList}>
          {(values.file?.mixer?.video || []).map((label, index) => {
            return (
              <div
                key={`video-${label}-${index}`}
                draggable
                onDragStart={e => {
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", index.toString());
                  e.dataTransfer.setData("type", "video");
                }}
                onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={e => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData("text/plain"));
                  const type = e.dataTransfer.getData("type");
                  if (type === "video") {
                    handleMixerVideoReorder(fromIndex, index);
                  }
                }}
                className={classes.videoItem}
              >
                <Typography variant="body2" className={classes.itemNumber}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" className={classes.itemLabel}>
                  {label}
                </Typography>
              </div>
            );
          })}
          {(values.file?.mixer?.video || []).length === 0 && (
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage(
                { id: "roomTemplate.mixer.video.empty" },
                { defaultMessage: "No video sources selected" },
              )}
            </Typography>
          )}
        </div>
      </DataGridItem>
      <DataGridItem big>
        <Typography variant="subtitle1" gutterBottom className={classes.sectionTitle}>
          {intl.formatMessage({ id: "roomTemplate.mixer.audio.title" }, { defaultMessage: "Audio Sources Order" })}
        </Typography>
        <Typography variant="caption" color="textSecondary" className={classes.sectionInstructions}>
          {intl.formatMessage(
            { id: "roomTemplate.mixer.audio.instructions" },
            { defaultMessage: "Drag and drop to reorder audio sources" },
          )}
        </Typography>
        <div className={classes.videoList}>
          {(values.file?.mixer?.audio || []).map((label, index) => {
            return (
              <div
                key={`audio-${label}-${index}`}
                draggable
                onDragStart={e => {
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", index.toString());
                  e.dataTransfer.setData("type", "audio");
                }}
                onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={e => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData("text/plain"));
                  const type = e.dataTransfer.getData("type");
                  if (type === "audio") {
                    handleMixerAudioReorder(fromIndex, index);
                  }
                }}
                className={classes.videoItem}
              >
                <Typography variant="body2" className={classes.itemNumber}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" className={classes.itemLabel}>
                  {label}
                </Typography>
              </div>
            );
          })}
          {(values.file?.mixer?.audio || []).length === 0 && (
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage(
                { id: "roomTemplate.mixer.audio.empty" },
                { defaultMessage: "No audio sources selected" },
              )}
            </Typography>
          )}
        </div>
      </DataGridItem>
    </>
  );
};

MixerSection.propTypes = {
  intl: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  allStreamSources: PropTypes.arrayOf(PropTypes.object).isRequired,
  setValue: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default injectIntl(withStyles(styles)(MixerSection));
